// Shared shell primitives — the one graphic language every module workspace
// is built from (Aaron, 2026-09-30): paper ground, white hairline cards,
// attention-amber panel heads, and the dark "viewscreen" that shows whatever
// the workspace is focused on. Modules compose these; they don't restyle them.
import { Link } from 'react-router-dom'
import { Sparkles, AlertTriangle } from 'lucide-react'

import { card, cardStyle } from './tokens'

export function DemoTag({ children = 'Demo data', tone = 'light' }) {
  const dark = tone === 'dark'
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] whitespace-nowrap"
      style={dark
        ? { background: 'rgba(0,0,0,0.35)', color: '#f6d9a0', border: '1px solid rgba(246,217,160,0.35)' }
        : { background: '#FBF1DD', color: 'var(--ns-attention)', border: '1px solid #EAD5AA' }}
    >
      {children}
    </span>
  )
}

export function Count({ n, tone = 'attention' }) {
  if (!n) return null
  return (
    <span className="shrink-0 rounded-full px-1.5 text-[10px] font-bold text-white tabular-nums" style={{ background: tone === 'janet' ? 'var(--ns-janet)' : 'var(--ns-attention)' }}>{n}</span>
  )
}

export function PanelHead({ icon, title, action, to, onAction, count }) {
  const Icon = icon
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon size={17} className="shrink-0" style={{ color: 'var(--ns-attention)' }} />}
        <h2 className="text-[15px] font-semibold truncate" style={{ color: 'var(--ns-ink)' }}>{title}</h2>
        <Count n={count} />
      </div>
      {action && (onAction
        ? <button type="button" onClick={onAction} className="shrink-0 text-[12px] font-semibold" style={{ color: 'var(--ns-janet)' }}>{action}</button>
        : <Link to={to || '#'} className="shrink-0 text-[12px] font-semibold" style={{ color: 'var(--ns-janet)' }}>{action}</Link>)}
    </div>
  )
}

export function Panel({ icon, title, action, to, onAction, count, label, className = '', children }) {
  return (
    <section className={`${card} p-4 ${className}`} style={cardStyle} aria-label={label || title}>
      <PanelHead icon={icon} title={title} action={action} to={to} onAction={onAction} count={count} />
      {children}
    </section>
  )
}

// ── viewscreen ───────────────────────────────────────────────────────────────
const TINTS = {
  briefing: ['#15122b', '#2d2458', '#6b4a8c'],
  client: ['#0f1d2a', '#1d3b4f', '#3f6f7d'],
  deal: ['#1f160f', '#3d2a1a', '#8a5a2b'],
  production: ['#101a17', '#1f3a31', '#4d7a5c'],
}

export function Backdrop({ tint = 'briefing' }) {
  const t = TINTS[tint] || TINTS.briefing
  return (
    <div className="absolute inset-0" aria-hidden="true" style={{ background: `linear-gradient(135deg, ${t[0]} 0%, ${t[1]} 55%, ${t[2]} 100%)` }}>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(60% 80% at 85% 10%, rgba(240,176,77,0.22), transparent 60%), radial-gradient(50% 70% at 10% 100%, rgba(139,122,230,0.25), transparent 60%)' }} />
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
    </div>
  )
}

// The dark focus surface — compact: it sizes to its content, and the space a
// backdrop used to fill goes to `aside`, a visual summary beside the text
// (below it on narrow widths). `tabs` is an optional [{ key, label, icon }]
// row; `background` replaces the tinted backdrop (e.g. property artwork).
export function Viewscreen({ tint, background, tabs, active, onTab, demo, label = 'Viewscreen', className = '', aside, children }) {
  return (
    <section className={`relative overflow-hidden rounded-2xl ${className}`} style={{ background: '#15122b' }} aria-label={label}>
      {background || <Backdrop tint={tint} />}
      <div className="relative flex flex-col gap-3 p-4 sm:p-5">
        {(tabs || demo) && (
          <div className="flex items-center gap-1.5 flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible -mx-1 px-1 pb-0.5" role={tabs ? 'tablist' : undefined} aria-label={tabs ? `${label} focus` : undefined}>
            {(tabs || []).map(t => {
              const Icon = t.icon || Sparkles
              const on = t.key === active
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => onTab?.(t.key)}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold transition-colors whitespace-nowrap"
                  style={on ? { background: 'white', color: '#1d1b16' } : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon size={12} /> {t.label}
                </button>
              )
            })}
            {demo && <span className="ml-auto shrink-0 pl-2"><DemoTag tone="dark" /></span>}
          </div>
        )}
        <div className={`grid gap-4 items-stretch ${aside ? 'lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-6' : ''}`}>
          <div className="min-w-0 flex flex-col gap-3 justify-end">{children}</div>
          {aside && (
            <div className="min-w-0 rounded-xl p-3 sm:p-4 flex flex-col gap-3 justify-center" style={{ background: 'rgba(10,6,20,0.38)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {aside}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export function Eyebrow({ children, tone = 'muted' }) {
  return (
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em]" style={{ color: tone === 'janet' ? '#c9bdfb' : 'rgba(255,255,255,0.6)' }}>
      {tone === 'janet' && <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#c9bdfb' }} />}
      {children}
    </div>
  )
}

export function Facts({ rows, max = 560 }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3" style={{ maxWidth: max }}>
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[10.5px] uppercase tracking-[0.14em] text-white/55">{k}</dt>
          <dd className="text-[14px] font-semibold text-white truncate">{v}</dd>
        </div>
      ))}
    </dl>
  )
}

export function Steps({ steps, at }) {
  return (
    <ol className="flex items-center gap-1.5 flex-wrap">
      {steps.map((s, i) => (
        <li key={s} className="flex items-center gap-1.5">
          <span
            className="rounded-full px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap"
            style={i === at
              ? { background: '#f0b04d', color: '#2a1a05' }
              : { background: i < at ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.08)', color: i < at ? 'white' : 'rgba(255,255,255,0.6)' }}
          >{s}</span>
          {i < steps.length - 1 && <span className="w-3 h-px bg-white/25" aria-hidden="true" />}
        </li>
      ))}
    </ol>
  )
}

export function NextMove({ children }) {
  return (
    <div className="flex items-start gap-2 rounded-xl px-3 py-2.5 max-w-[640px]" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)' }}>
      <Sparkles size={15} className="shrink-0 mt-0.5" style={{ color: '#c9bdfb' }} />
      <p className="text-[13px] text-white/90">{children}</p>
    </div>
  )
}

export function StatTiles({ stats, max = 760 }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" style={{ maxWidth: max }}>
      {stats.map(s => (
        <div key={s.key || s.label} className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div className="text-[24px] font-semibold tabular-nums leading-none" style={{ color: s.tone === 'attention' ? '#f0c27a' : 'white' }}>{s.value}</div>
          <div className="mt-1 text-[11px] text-white/65">{s.label}</div>
        </div>
      ))}
    </div>
  )
}

export function Chip({ on, dot, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold whitespace-nowrap"
      style={on
        ? { background: 'var(--ns-ink)', color: 'white', borderColor: 'var(--ns-ink)' }
        : { background: 'white', color: 'var(--ns-ink-2)', borderColor: 'var(--ns-hair)' }}
    >
      {dot && <span className="w-2 h-2 rounded-full" style={{ background: dot }} />}
      {children}
    </button>
  )
}

export function PrimaryButton({ icon, children, onClick }) {
  const Icon = icon
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold text-white" style={{ background: 'var(--ns-janet)' }}>
      {Icon && <Icon size={14} />} {children}
    </button>
  )
}

// A section's state, in the page's voice: loading, could-not-be-read (never
// dressed up as empty), or genuinely empty. `action` is an optional door.
export function StateNote({ state, empty, unavailable = 'This could not be read — the page will not guess at it.', loading = 'Reading…', action }) {
  if (state === 'loading') return <p className="text-[12.5px] italic" style={{ color: 'var(--ns-faint)' }}>{loading}</p>
  if (state === 'unavailable' || state === 'not-connected') {
    return (
      <p className="flex items-start gap-1.5 text-[12.5px]" style={{ color: 'var(--ns-attention)' }}>
        <AlertTriangle size={14} className="shrink-0 mt-0.5" /> <span>{unavailable}{action && <> {action}</>}</span>
      </p>
    )
  }
  return <p className="text-[12.5px]" style={{ color: 'var(--ns-gray)' }}>{empty}{action && <> {action}</>}</p>
}
